import { dayjs } from "@holibob-packages/dayjs";

import { MAX_DURATION, computeEffectiveCancellationPenalty } from "./computeEffectiveCancellationPenalty";
import {
    computePersonListEffectiveCancellationPenalty,
    ComputePersonListEffectiveCancellationPenalty,
} from "./computePersonListEffectiveCancellationPenalty";
import { CancellationPenalty, Price } from "./types";

export type ComputeBookingAvailabilityPersonCancellationPenaltyListProps = Array<{
    penaltyList: CancellationPenalty[];
    totalPrice: Price;
}>;

export function computeBookingAvailabilityPersonCancellationPenaltyList(
    personList: ComputeBookingAvailabilityPersonCancellationPenaltyListProps
): CancellationPenalty[] {
    const allPenaltyList = personList.map(({ penaltyList }) => penaltyList).flat();

    // Cannot combine different types of cancellation penalties
    if ([...new Set(allPenaltyList.map(({ relativeTo }) => relativeTo))].length > 1) {
        return [];
    }

    const effectiveRelativeTo = allPenaltyList[0]?.relativeTo;

    if (!effectiveRelativeTo) {
        return [];
    }

    // dates do not matter
    const dStartAt = dayjs("2024-01-01T00:00:00.000Z");
    const dBookingDate = dayjs("2024-01-01T00:00:00.000Z");
    const dStart = effectiveRelativeTo === "TRAVEL_DATE" ? dStartAt : dBookingDate;

    // Unique list of durations to iterate on
    const durationListSorted = allPenaltyList
        .map(({ duration }) => duration ?? MAX_DURATION)
        .sort((durationA, durationAB) => {
            const dADuration = dayjs.duration(durationA).asMilliseconds();
            const dBDuration = dayjs.duration(durationAB).asMilliseconds();
            return dADuration - dBDuration;
        });

    const durationList = [...new Set(durationListSorted)];

    const computedPenaltyList: CancellationPenalty[] = [];

    // emulate dNow to calculate effective policy for each duration
    for (const duration of durationList) {
        const dDuration = dayjs.duration(duration);
        const dNow = effectiveRelativeTo === "TRAVEL_DATE" ? dStart.subtract(dDuration) : dStart.add(dDuration);
        const personEffectiveCancellationPenaltyList: ComputePersonListEffectiveCancellationPenalty[] = [];

        for (const person of personList) {
            const penaltyList = person.penaltyList;
            const penalty = computeEffectiveCancellationPenalty({
                penaltyList,
                dNow,
                dBookingDate,
                dStartAt,
            });

            // If at least one person is not cancellable it makes entire list not cancellable
            if (!penalty) {
                return [];
            }

            personEffectiveCancellationPenaltyList.push({
                ...penalty,
                totalAmount: person.totalPrice.gross,
                totalCurrency: person.totalPrice.currency,
            });
        }

        const effectivePenalty = computePersonListEffectiveCancellationPenalty(personEffectiveCancellationPenaltyList);

        if (!effectivePenalty) {
            return [];
        }

        computedPenaltyList.push(effectivePenalty);
    }

    return computedPenaltyList;
}
